import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { has, isObject } from 'lodash-es';
import { switchMap } from 'rxjs/operators';
import { ApiServiceResponse, ApiStatus } from 'src/app/core/api/api.models';
import { ApiService } from 'src/app/core/api/api.service';
import { LocaleService } from 'src/app/core/locale/locale.service';
import { showNotificationAction } from 'src/app/core/notification/notification.actions';
import { NotificationType } from 'src/app/core/notification/notification.state';
import { hideModifyPageSpinner } from 'src/app/core/page-spinner/page-spinner.actions';
import { routeTo } from 'src/app/core/routing/routing.actions';
import { CartSummaryResponse } from 'src/app/feature/cart/models/cart-summary-response.model';
import {
	addCartItemRequested,
	addCartItemSuccess,
	getCartSummaryRequested,
	getCartSummarySuccess,
	getDynamicPricingAdjustmentFailed,
	getDynamicPricingAdjustmentRequested,
	GetDynamicPricingAdjustmentSuccess,
	getNewCartIDFailed,
	getNewCartIDReceived,
	getNewCartIDRequested,
	updateCartItemsFailed,
	updateCartItemsRequested,
	updateCartItemsSuccess,
} from 'src/app/feature/cart/redux/cart.actions';
import { ApiRequestType } from 'src/app/shared/enums/api.enums';

@Injectable({
	providedIn: 'root',
})
export class CartEffects {
	getNewCartIDRequested$ = createEffect(() =>
		this._actions$.pipe(
			ofType(getNewCartIDRequested),
			switchMap((action) => {
				const params = {};
				return this._apiService
					.post<{ SERVICE: ApiServiceResponse }>(ApiRequestType.GET_NEW_CART_ID, params, false)
					.pipe(
						switchMap((response) => {
							if (!has(response, 'SERVICE') || !isObject(response.SERVICE)) {
								return [getNewCartIDFailed()];
							}

							return [
								getNewCartIDReceived({
									cartId: response.SERVICE.cart_id,
									cartKey: response.SERVICE.cart_key,
								}),
							];
						})
					);
			})
		)
	);

	getDynamicPricingAdjustmentRequested$ = createEffect(() =>
		this._actions$.pipe(
			ofType(getDynamicPricingAdjustmentRequested),
			switchMap((action) => {
				const params = {
					...action,
				};

				return this._apiService
					.post<{ SERVICE: ApiServiceResponse }>(ApiRequestType.GET_DYNAMIC_PRICING_ADJUSTMENT, params, false)
					.pipe(
						switchMap((response) => {
							if (
								!has(response, 'SERVICE') ||
								!isObject(response.SERVICE) ||
								response?.SERVICE?.status === ApiStatus.FAILED
							) {
								return [getDynamicPricingAdjustmentFailed()];
							}

							return [GetDynamicPricingAdjustmentSuccess()];
						})
					);
			})
		)
	);

	addCartItemsRequested$ = createEffect(() =>
		this._actions$.pipe(
			ofType(addCartItemRequested),
			switchMap((action) => {
				const params = {
					...action,
				};
				delete params.type;
				return this._apiService
					.post<{ SERVICE: ApiServiceResponse }>(ApiRequestType.ADD_CART_ITEM, params, false)
					.pipe(
						switchMap((response) => {
							if (
								!has(response, 'SERVICE') ||
								!isObject(response.SERVICE) ||
								response?.SERVICE?.error_code ||
								response?.SERVICE?.status === ApiStatus.FAILED
							) {
								return [
									hideModifyPageSpinner({ initiator: 'AddCartItem request failed' }),
									showNotificationAction({
										buttonLabel: this._locale.get('common.close'),
										dialogType: NotificationType.GENERAL,
										initiator: `AddCartItem Failed`,
										message: this._locale.get(`addcartitem.${response?.SERVICE?.error_code}`),
									}),
								];
							}

							return [addCartItemSuccess()];
						})
					);
			})
		)
	);

	updateCartItemsRequested$ = createEffect(() =>
		this._actions$.pipe(
			ofType(updateCartItemsRequested),
			switchMap((action) => {
				const params = {
					...action,
				};
				return this._apiService
					.post<{ SERVICE: ApiServiceResponse }>(ApiRequestType.UPDATE_CART_ITEMS, params, false)
					.pipe(
						switchMap((response) => {
							if (
								!has(response, 'SERVICE') ||
								!isObject(response.SERVICE) ||
								response?.SERVICE?.status === ApiStatus.FAILED
							) {
								return [
									hideModifyPageSpinner({ initiator: 'UpdateCartItems request failed' }),
									updateCartItemsFailed(),
									showNotificationAction({
										buttonLabel: this._locale.get('common.close'),
										dialogType: NotificationType.GENERAL,
										initiator: `UpdateCartItems Failed`,
										message: this._locale.get(`error.updatecartitems`),
									}),
								];
							}
							return [updateCartItemsSuccess()];
						})
					);
			})
		)
	);

	getCartSummaryRequested$ = createEffect(() =>
		this._actions$.pipe(
			ofType(getCartSummaryRequested),
			switchMap((action) => {
				const params = {
					...action,
					portal_upgrade: 1,
				};
				delete params.type;
				return this._apiService
					.post<{ SERVICE: ApiServiceResponse }>(ApiRequestType.GET_CART_SUMMARY, params, false)
					.pipe(
						switchMap((response) => {
							if (
								!has(response, 'SERVICE') ||
								!isObject(response.SERVICE) ||
								response?.SERVICE?.error_code
							) {
								return [
									routeTo({ payload: ['booking-portal', 'modify'] }),
									showNotificationAction({
										buttonLabel: this._locale.get('common.close'),
										dialogType: NotificationType.GENERAL,
										initiator: `GetCartSummary Failed`,
										message: this._locale
											.get(`getcartsummary.error`)
											.replace('@@errorcode', response?.SERVICE?.error_code),
									}),
								];
							}

							const summary = response.SERVICE as unknown as CartSummaryResponse;

							return [getCartSummarySuccess({ summary })];
						})
					);
			})
		)
	);
	/**
	 * Our effects constructor
	 * @param _actions$ The action stream
	 * @param _apiService the API service
	 * @param _locale the locale service
	 */
	constructor(private _actions$: Actions, private _apiService: ApiService, private _locale: LocaleService) {}
}
